import React, { useState, useEffect, useRef } from "react";
import { Icon, Loader } from "semantic-ui-react";
import classNames from "classnames";
import { filter, groupBy, keyBy, map, minBy, orderBy, size, sumBy, uniqBy } from "lodash";
import moment from "moment";
import { HeaderPage } from "../../components/Admin";
import { useOrder, usePayment, useTable, usePolling, useNow } from "../../hooks";
import { getTablePlace } from "../../utils/table";
import { getOrderPrice } from "../../utils/order";
import { formatElapsed } from "../../utils/date";
import { getDeliveryChannel, getPaymentMethod, ORDER_STATUS } from "../../utils/constants";
import { TableDetailsAdmin } from "./TableDetailsAdmin";
import "./CashCharge.scss";

// Pestañas como el "Cobrar" de la Caja de Restaurant.pe. WhatsApp (llegó por
// mensaje y recoge) va con Rápida: es un pedido para llevar.
const TABS = [
  { key: "salon", text: "Salón", kinds: ["SALON"] },
  { key: "quick", text: "Rápida", kinds: ["TAKEAWAY", "WHATSAPP"] },
  { key: "delivery", text: "Delivery", kinds: ["DELIVERY"] },
];

const kindOf = (table) => table.kind || "SALON";

// Caja → Cobrar: pantalla del cajero. A la izquierda, las mesas con consumo
// sin cobrar (no solo las que pidieron la cuenta); a la derecha, la cuenta de
// la elegida, sin salir de la pantalla (TableDetailsAdmin incrustado).
export function CashCharge() {
  const { tables, getTables } = useTable();
  const { getOpenOrders } = useOrder();
  const { getPendingPayments } = usePayment();
  const [orders, setOrders] = useState(null);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState("salon");
  // Filtro por ambiente dentro de Salón (solo con dos o más ambientes).
  const [areaFilter, setAreaFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const detailRef = useRef(null);
  const now = useNow();

  const load = async () => {
    try {
      const [openOrders, pending] = await Promise.all([getOpenOrders(), getPendingPayments()]);
      setOrders(openOrders);
      setPayments(pending);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getTables();
  }, []);
  usePolling(load, 10000, { immediate: true });

  const ordersByTable = groupBy(orders, "table");
  const paymentByTable = keyBy(payments, "table");
  const busyTables = filter(tables, (table) => ordersByTable[table.id] || paymentByTable[table.id]);
  const entries = map(busyTables, (table) => {
    const tableOrders = ordersByTable[table.id] || [];
    const payment = paymentByTable[table.id];
    const first = minBy(tableOrders, (order) => moment(order.created_at).valueOf());
    return {
      table,
      orders: tableOrders,
      payment,
      // Delivery sin cuenta todavía: el envío se suma igual que lo hará la cuenta.
      total: payment
        ? Number(payment.totalPayment)
        : sumBy(tableOrders, getOrderPrice) +
          (table.kind === "DELIVERY" ? Number(table.current_delivery_fee || 0) : 0),
      since: first?.created_at || payment?.created_at,
      ready: filter(tableOrders, (order) => order.status === ORDER_STATUS.READY).length,
    };
  });
  // Posición de cada mesa en la lista de la API (ya viene por ambiente y número).
  const position = {};
  (tables || []).forEach((table, index) => {
    position[table.id] = index;
  });
  const byTab = (key) => {
    const { kinds } = TABS.find((item) => item.key === key);
    return orderBy(
      filter(entries, (entry) => kinds.includes(kindOf(entry.table))),
      [(entry) => (entry.payment ? 0 : 1), (entry) => position[entry.table.id]],
      ["asc", "asc"]
    );
  };
  const areas = uniqBy(
    filter(tables, (table) => kindOf(table) === "SALON" && table.area).map((table) => ({
      id: String(table.area),
      name: table.area_name,
    })),
    "id"
  );
  const multiArea = size(areas) > 1;
  const activeArea = multiArea && areas.some((a) => a.id === areaFilter) ? areaFilter : "all";
  const visible = byTab(tab).filter(
    (entry) => tab !== "salon" || activeArea === "all" || String(entry.table.area) === activeArea
  );

  const select = (id) => {
    setSelectedId(id);
    // En celular los paneles van uno debajo del otro: bajar a la cuenta.
    setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const onClosed = () => {
    setSelectedId(null);
    load();
  };

  return (
    <>
      <HeaderPage title="Cobrar" />

      <div className="cash-charge">
        <div className="cash-charge__list">
          <div className="cash-charge__tabs">
            {TABS.map((item) => {
              const count = size(byTab(item.key));
              return (
                <button
                  key={item.key}
                  type="button"
                  className={classNames("cash-charge__tab", {
                    "cash-charge__tab--active": tab === item.key,
                  })}
                  onClick={() => setTab(item.key)}
                >
                  {item.text}
                  {count > 0 && <span className="cash-charge__count">{count}</span>}
                </button>
              );
            })}
          </div>

          {tab === "salon" && multiArea && (
            <div className="cash-charge__areas">
              {[{ id: "all", name: "Todos" }, ...areas].map((area) => {
                const count = size(
                  byTab("salon").filter((e) => area.id === "all" || String(e.table.area) === area.id)
                );
                return (
                  <button
                    key={area.id}
                    type="button"
                    className={classNames("cash-charge__area", {
                      "cash-charge__area--active": activeArea === area.id,
                    })}
                    onClick={() => setAreaFilter(area.id)}
                  >
                    {area.name}
                    {count > 0 && <span className="cash-charge__count">{count}</span>}
                  </button>
                );
              })}
            </div>
          )}

          {orders === null ? (
            <Loader active inline="centered">
              Cargando...
            </Loader>
          ) : size(visible) === 0 ? (
            <p className="cash-charge__empty">No hay cuentas por cobrar aquí.</p>
          ) : (
            <div className="cash-charge__tables">
              {visible.map((entry) => {
                const channel =
                  entry.table.kind === "DELIVERY" &&
                  getDeliveryChannel(entry.payment?.delivery_channel || entry.table.current_delivery_channel);
                return (
                  <button
                    key={entry.table.id}
                    type="button"
                    className={classNames("cash-charge__table", {
                      "cash-charge__table--payment": entry.payment,
                      "cash-charge__table--selected": selectedId === entry.table.id,
                    })}
                    onClick={() => select(entry.table.id)}
                  >
                    <span className="cash-charge__table-orders">
                      {size(entry.orders)} ped.
                    </span>
                    <strong className="cash-charge__table-name">{getTablePlace(entry.table)}</strong>
                    <span className="cash-charge__table-total">S/ {entry.total.toFixed(2)}</span>
                    {channel && <span className="cash-charge__table-meta">{channel.text}</span>}
                    <span className="cash-charge__table-status">
                      {entry.payment ? (
                        // La forma de pago que avisó al pedir la cuenta (se
                        // puede cambiar al cobrar).
                        <>
                          <Icon name={getPaymentMethod(entry.payment.paymentType)?.icon} />
                          Cuenta · {getPaymentMethod(entry.payment.paymentType)?.text}
                          {entry.payment.requested_doc_type === "FACTURA" && " · Factura"}
                          {entry.payment.requested_doc_type === "BOLETA" && " · Boleta"}
                        </>
                      ) : (
                        <>
                          <Icon name="clock outline" />
                          {entry.since ? formatElapsed(entry.since, now) : "Pendiente"}
                        </>
                      )}
                    </span>
                    {entry.ready > 0 && (
                      <span className="cash-charge__table-ready">{entry.ready} listo(s)</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="cash-charge__detail" ref={detailRef}>
          {selectedId ? (
            <TableDetailsAdmin tableId={selectedId} embedded onClosed={onClosed} />
          ) : (
            <div className="cash-charge__placeholder">
              <h3>Selecciona una mesa</h3>
              <Icon name="arrow left" size="big" />
              <p>Elige una mesa de la lista para ver su cuenta y cobrarla.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
